import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { Cookie, Shield, BarChart3, Megaphone, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'cantiereai_cookie_consent';

const DEFAULT_CATEGORIES = { necessary: true, analytics: false, marketing: false };

export function getConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

export function hasConsentFor(category) {
    const c = getConsent();
    if (!c) return category === 'necessary';
    return Boolean(c.categories?.[category]);
}

async function persistConsent({ consent_id, categories, accepted_all = false, rejected_all = false, source = 'banner' }) {
    const payload = {
        consent_id: consent_id || undefined,
        categories,
        accepted_all,
        rejected_all,
        policy_version: '1.0.0',
        source,
        page: typeof window !== 'undefined' ? window.location.pathname : '/',
    };
    try {
        const res = await axios.post(`${BACKEND_URL}/api/consent/cookie`, payload);
        const final = res.data;
        const localData = {
            consent_id: final.consent_id,
            categories: final.categories,
            policy_version: final.policy_version,
            saved_at: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(localData));
        // notify other tabs
        window.dispatchEvent(new CustomEvent('cantiereai-consent-changed', { detail: localData }));
        return localData;
    } catch (e) {
        // graceful fallback: still save locally even if backend fails (UX > backend log)
        const localData = {
            consent_id: consent_id || crypto.randomUUID(),
            categories,
            policy_version: '1.0.0',
            saved_at: new Date().toISOString(),
            offline: true,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(localData));
        return localData;
    }
}

export function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [showPrefs, setShowPrefs] = useState(false);
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        const stored = getConsent();
        if (!stored || stored.policy_version !== '1.0.0') {
            // Show with a small delay so page is interactive first (perceived perf)
            const t = setTimeout(() => setVisible(true), 600);
            return () => clearTimeout(t);
        }
        setCategories({ ...DEFAULT_CATEGORIES, ...(stored.categories || {}) });
    }, []);

    const acceptAll = async () => {
        setLoading(true);
        await persistConsent({
            consent_id: getConsent()?.consent_id,
            categories: { necessary: true, analytics: true, marketing: true },
            accepted_all: true,
            source: 'banner',
        });
        setVisible(false);
        setLoading(false);
    };

    const rejectAll = async () => {
        setLoading(true);
        await persistConsent({
            consent_id: getConsent()?.consent_id,
            categories: { necessary: true, analytics: false, marketing: false },
            rejected_all: true,
            source: 'banner',
        });
        setVisible(false);
        setLoading(false);
    };

    const savePrefs = async () => {
        setLoading(true);
        await persistConsent({
            consent_id: getConsent()?.consent_id,
            categories,
            source: 'preferences',
        });
        setShowPrefs(false);
        setVisible(false);
        setLoading(false);
    };

    if (!visible && !showPrefs) return null;

    return (
        <>
            {visible && !showPrefs && (
                <div
                    role="dialog"
                    aria-label="Informativa cookie"
                    className="fixed bottom-0 left-0 right-0 z-[10001] bg-[hsl(220_13%_10%)] text-white border-t-4 border-[hsl(var(--accent))] shadow-[0_-8px_24px_rgba(0,0,0,0.3)]"
                    data-testid="cookie-banner"
                >
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 md:py-5 flex flex-col md:flex-row gap-4 items-start md:items-center">
                        <div className="flex items-start gap-3 flex-1">
                            <Cookie className="w-7 h-7 text-[hsl(var(--accent))] flex-shrink-0 mt-1" />
                            <div className="text-sm">
                                <div className="font-display text-lg mb-1">{t('cookie.title')}</div>
                                <p className="text-white/80 leading-snug">
                                    <Trans
                                        i18nKey="cookie.description"
                                        components={{
                                            cookies: <Link to="/cookies" className="text-[hsl(var(--accent))] underline" />,
                                            privacy: <Link to="/privacy" className="text-[hsl(var(--accent))] underline" />,
                                        }}
                                        values={{ cookies: 'Cookie Policy', privacy: 'Privacy Policy' }}
                                    />
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 w-full md:w-auto md:flex-nowrap">
                            <Button
                                variant="outline"
                                onClick={rejectAll}
                                disabled={loading}
                                className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white flex-1 md:flex-none"
                                data-testid="cookie-reject-all"
                            >{t('cookie.reject_all')}</Button>
                            <Button
                                variant="outline"
                                onClick={() => setShowPrefs(true)}
                                disabled={loading}
                                className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white flex-1 md:flex-none"
                                data-testid="cookie-preferences"
                            >{t('cookie.customize')}</Button>
                            <Button
                                onClick={acceptAll}
                                disabled={loading}
                                className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold flex-1 md:flex-none"
                                data-testid="cookie-accept-all"
                            >{t('cookie.accept_all')}</Button>
                        </div>
                    </div>
                </div>
            )}

            <Dialog open={showPrefs} onOpenChange={(o) => setShowPrefs(o)}>
                <DialogContent className="max-w-lg" data-testid="cookie-preferences-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2"><Cookie className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('cookie.prefs_title')}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('cookie.prefs_subtitle')}</p>
                    <div className="space-y-3 mt-3">
                        {/* Necessary */}
                        <div className="p-4 rounded-lg border bg-[hsl(var(--muted))]/40">
                            <div className="flex items-start justify-between gap-3 mb-1">
                                <div className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-[hsl(145_55%_30%)]" />
                                    <span className="font-semibold text-sm">{t('cookie.cat_necessary')}</span>
                                </div>
                                <span className="text-xs text-[hsl(var(--muted-foreground))] font-semibold">{t('cookie.always_on')}</span>
                            </div>
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('cookie.necessary_desc')}</p>
                        </div>
                        {/* Analytics */}
                        <label className="p-4 rounded-lg border block cursor-pointer hover:border-[hsl(var(--accent))] transition-colors" data-testid="cookie-cat-analytics">
                            <div className="flex items-start justify-between gap-3 mb-1">
                                <div className="flex items-center gap-2">
                                    <BarChart3 className="w-4 h-4 text-[hsl(210_80%_45%)]" />
                                    <span className="font-semibold text-sm">{t('cookie.cat_analytics')}</span>
                                </div>
                                <Switch
                                    checked={categories.analytics}
                                    onCheckedChange={(v) => setCategories({ ...categories, analytics: v })}
                                />
                            </div>
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('cookie.analytics_desc')}</p>
                        </label>
                        {/* Marketing */}
                        <label className="p-4 rounded-lg border block cursor-pointer hover:border-[hsl(var(--accent))] transition-colors" data-testid="cookie-cat-marketing">
                            <div className="flex items-start justify-between gap-3 mb-1">
                                <div className="flex items-center gap-2">
                                    <Megaphone className="w-4 h-4 text-[hsl(280_60%_45%)]" />
                                    <span className="font-semibold text-sm">{t('cookie.cat_marketing')}</span>
                                </div>
                                <Switch
                                    checked={categories.marketing}
                                    onCheckedChange={(v) => setCategories({ ...categories, marketing: v })}
                                />
                            </div>
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('cookie.marketing_desc')}</p>
                        </label>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <Button variant="outline" onClick={rejectAll} disabled={loading} className="flex-1" data-testid="cookie-prefs-reject">{t('cookie.reject_all')}</Button>
                        <Button onClick={savePrefs} disabled={loading} className="flex-1 bg-[hsl(220_13%_10%)] text-white" data-testid="cookie-prefs-save">{t('cookie.save_prefs')}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}


export function CookieSettingsLink({ className = '' }) {
    const { t } = useTranslation();
    const open = (e) => {
        e?.preventDefault();
        // Reset to make banner show again
        localStorage.removeItem(STORAGE_KEY);
        window.location.reload();
    };
    return (
        <a href="#" onClick={open} className={className} data-testid="cookie-settings-link">
            {t('footer.manage_cookies')}
        </a>
    );
}
